import React, { useRef, useEffect, useState } from "react";
import NavigationBar from "./NavigationBar";
import StatusBar from "./StatusBar";
import { useAuth } from "../contexts/AuthContext";

interface LayoutProps {
  children: React.ReactNode;
}

function Layout({ children }: LayoutProps) {
  const { isAuthenticated } = useAuth();
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
      }
    };

    // Initial measurement
    updateHeaderHeight();

    // Update on window resize
    window.addEventListener("resize", updateHeaderHeight);

    // Update on status bar visibility changes
    const resizeObserver = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div className="min-h-screen w-full bg-slate-900 relative">
      {/* Side borders - hidden on mobile for full width */}
      <div className="hidden md:block absolute left-0 top-0 w-1 h-full bg-slate-600"></div>
      <div className="hidden md:block absolute right-0 top-0 w-1 h-full bg-slate-600"></div>

      {/* Fixed Header Container */}
      <div
        ref={headerRef}
        data-header-container
        className="fixed top-0 left-0 right-0 z-50"
      >
        <NavigationBar />
        {isAuthenticated && <StatusBar />}
      </div>

      {/* Main Content - with dynamic top padding based on actual header height */}
      <main className="w-full" style={{ paddingTop: `${headerHeight}px` }}>
        {isAuthenticated ? (
          children
        ) : (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white mb-4">Command Legacy</h1>
              <p className="text-gray-300 text-lg mb-8">Police Leadership Platform</p>
              <p className="text-gray-400 mb-8">Sign in to access your professional law enforcement resources</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Layout;
